import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { t as tr } from '@/lib/i18n';
import { format } from 'date-fns';
import { Download, Inbox, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/AppShell';
import { PriorityBadge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { EmptyState, TBody, TD, TH, THead, TR, Table } from '@/components/ui/Table';
import { api } from '@/lib/api';
const STATUS_OPTIONS = [
    ['', 'All statuses'],
    ['new', 'New'],
    ['open', 'Open'],
    ['in_progress', 'In progress'],
    ['on_hold', 'On hold'],
    ['pending_approval', 'Pending approval'],
    ['resolved', 'Resolved'],
    ['closed', 'Closed'],
    ['cancelled', 'Cancelled'],
];
function csvEscape(v) {
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
export function MyTicketsPage() {
    const navigate = useNavigate();
    // Phase 3 — organization view: a client's PRIMARY contact may widen the list
    // to every ticket of their company. The backend enforces eligibility; this
    // context call only decides whether to SHOW the toggle.
    const orgCtx = useQuery({
        queryKey: ['portal', 'org-context'],
        queryFn: () => api.get('/portal/org-context'),
        staleTime: 5 * 60 * 1000,
    });
    const [scope, setScope] = useState('mine');
    // Backend scopes /tickets to "where requester = me" for Customer User role
    // (or their whole client org when scope=organization + primary contact).
    // For other personas viewing this page, the standard tenant scope applies.
    const list = useQuery({
        queryKey: ['portal', 'my-tickets', scope],
        // Endpoint now returns a paginated envelope; peel `.items` so the
        // rest of this component continues to consume Ticket[].
        queryFn: async () => (await api.get(`/tickets?limit=200${scope === 'organization' ? '&scope=organization' : ''}`))
            .items,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
        refetchInterval: 20_000,
    });
    // Phase 2 — client-side search + status filter over the loaded rows.
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');
    const tickets = useMemo(() => {
        let rows = list.data ?? [];
        if (status)
            rows = rows.filter((t) => t.status === status);
        const needle = q.trim().toLowerCase();
        if (needle) {
            rows = rows.filter((t) => t.ticket_number.toLowerCase().includes(needle) || (t.title ?? '').toLowerCase().includes(needle));
        }
        return rows;
    }, [list.data, q, status]);
    const exportCsv = () => {
        const header = 'Number,Title,Priority,Status,Created,Last update';
        const lines = tickets.map((t) => [
            t.ticket_number,
            csvEscape(t.title ?? ''),
            t.priority,
            t.status,
            format(new Date(t.created_at), 'yyyy-MM-dd HH:mm'),
            format(new Date(t.updated_at ?? t.created_at), 'yyyy-MM-dd HH:mm'),
        ].join(','));
        const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'my-tickets.csv';
        a.click();
        URL.revokeObjectURL(url);
    };
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: scope === 'organization' ? `${orgCtx.data?.client_name ?? 'Organization'} tickets` : 'My tickets', description: scope === 'organization'
                    ? "Every ticket raised by your organization (you're the primary contact)."
                    : "Tickets you've raised.", actions: _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { variant: "ghost", onClick: exportCsv, disabled: tickets.length === 0, children: [_jsx(Download, { size: 16 }), tr('Export CSV')] }), _jsx(Link, { to: "/portal/tickets/new", children: _jsxs(Button, { children: [_jsx(Plus, { size: 16 }), tr('New request')] }) })] }) }), _jsxs(PageBody, { children: [orgCtx.data?.is_org_viewer && (_jsx("div", { className: "mb-3 inline-flex overflow-hidden rounded-md border border-slate-200 text-xs dark:border-slate-700", children: ['mine', 'organization'].map((s) => (_jsx("button", { type: "button", onClick: () => setScope(s), className: 'px-3 py-1.5 ' +
                                (scope === s
                                    ? 'bg-brand-500 font-medium text-white'
                                    : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'), children: s === 'mine' ? 'My tickets' : `My organization${orgCtx.data?.client_name ? ` (${orgCtx.data.client_name})` : ''}` }, s))) })), _jsxs("div", { className: "mb-3 flex flex-wrap items-center gap-2", children: [_jsx(Input, { value: q, onChange: (e) => setQ(e.target.value), placeholder: tr('Search by number or title…'), className: "max-w-xs" }), _jsx(Select, { value: status, onChange: (e) => setStatus(e.target.value), className: "w-44", children: STATUS_OPTIONS.map(([v, label]) => (_jsx("option", { value: v, children: label }, v))) }), (q || status) && (_jsx("button", { className: "text-sm text-slate-500 hover:underline", onClick: () => {
                                    setQ('');
                                    setStatus('');
                                }, children: tr('Clear') }))] }), list.isLoading && (_jsx("div", { className: "space-y-2", children: Array.from({ length: 4 }).map((_, i) => (_jsx("div", { className: "h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800/50" }, i))) })), list.isError && (_jsx(EmptyState, { icon: _jsx(Inbox, { className: "h-6 w-6" }), message: "Couldn't load your tickets. Please try again.", cta: _jsx(Button, { variant: "outline", onClick: () => list.refetch(), children: "Retry" }) })), !list.isLoading && !list.isError && tickets.length === 0 && (_jsx(EmptyState, { icon: _jsx(Inbox, { className: "h-6 w-6" }), message: q || status
                            ? 'No tickets match your filter.'
                            : "You haven't raised any tickets yet.", cta: q || status ? undefined : (_jsx(Link, { to: "/portal/tickets/new", children: _jsxs(Button, { children: [_jsx(Plus, { size: 16 }), "Submit a request"] }) })) })), tickets.length > 0 && (_jsxs(Table, { children: [_jsx(THead, { children: _jsxs(TR, { children: [_jsx(TH, { children: "Number" }), _jsx(TH, { children: "Title" }), _jsx(TH, { children: "Priority" }), _jsx(TH, { children: "Status" }), _jsx(TH, { children: "Created" }), _jsx(TH, { children: "Last update" })] }) }), _jsx(TBody, { children: tickets.map((t) => (_jsxs(TR, { onClick: () => navigate(`/tickets/${t.id}`), children: [_jsx(TD, { className: "font-mono text-xs font-medium text-brand-600 dark:text-brand-400", children: t.ticket_number }), _jsx(TD, { className: "font-medium text-slate-900 dark:text-slate-100", children: t.title }), _jsx(TD, { children: _jsx(PriorityBadge, { priority: t.priority }) }), _jsx(TD, { children: _jsx(StatusBadge, { status: t.status, substatus: t.substatus }) }), _jsx(TD, { className: "text-xs text-slate-500", children: format(new Date(t.created_at), 'MMM d HH:mm') }), _jsx(TD, { className: "text-xs text-slate-500", children: format(new Date(t.updated_at ?? t.created_at), 'MMM d HH:mm') })] }, t.id))) })] }))] })] }));
}
